import React, { useEffect, useState } from "react";
import { clearTokens } from "@/utils/tokenService";
import {
  clearImpersonation,
  endImpersonationOnServer,
  getImpersonation,
} from "@/utils/impersonation";

const formatRemaining = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, "0")}`;
};

/**
 * Always-visible reminder that a super admin is acting as this counselor, with
 * the time left and an Exit button. Renders nothing for ordinary sessions.
 */
export const ImpersonationBanner: React.FC = () => {
  const [info] = useState(() => getImpersonation());
  const [remaining, setRemaining] = useState(() =>
    info ? new Date(info.expiresAt).getTime() - Date.now() : 0,
  );
  const [exiting, setExiting] = useState(false);

  const exit = async () => {
    if (exiting) return;
    setExiting(true);
    await endImpersonationOnServer(); // needs the token, so before clearing it
    clearImpersonation();
    clearTokens();
    window.close(); // works because the admin portal opened this tab
    // If the browser refuses to close it, land on the login screen.
    window.location.replace("/");
  };

  useEffect(() => {
    if (!info) return;
    const timer = window.setInterval(() => {
      setRemaining(new Date(info.expiresAt).getTime() - Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [info]);

  useEffect(() => {
    if (info && remaining <= 0) void exit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining <= 0]);

  if (!info) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-[2000] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-full bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-lg"
    >
      <span className="truncate">
        Viewing as <strong>{info.targetName || "this counselor"}</strong>
        {info.actorName ? ` · ${info.actorName}` : ""}
      </span>
      <span className="shrink-0 tabular-nums opacity-90">{formatRemaining(remaining)} left</span>
      <button
        type="button"
        onClick={exit}
        disabled={exiting}
        className="shrink-0 rounded-full bg-white/90 px-3 py-0.5 text-xs font-semibold text-amber-700 hover:bg-white disabled:opacity-60"
      >
        {exiting ? "Exiting…" : "Exit"}
      </button>
    </div>
  );
};
